import React, { useContext, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { AuthContext } from '../../context/auth.context'
import { OTHER_SESSIONS_STAY, signOutSessionBody, YOUR_OTHER_SESSIONS_STAY } from '../session/sign-out'
import { Pill } from '../sso-workspace/pill'
import { ConfirmDialog } from '../sso-workspace/confirm-dialog'
import { formatAgo, formatCount } from '../sso-workspace/format'
import { getEntry, signOutSession } from './audit-api'
import {
  actorFilterKey,
  actorView,
  auditErrorText,
  canSignOutSession,
  categoryLabel,
  changeView,
  categoryTone,
  daySentence,
  exactUTC,
  firstName,
  inlineChanges,
  isSelfRecord,
  locationText,
  recordAfterSentence,
  recordView,
  viaText
} from './entry-format'
import { entryPath } from './record-links'
import { requestChangesHref, sessionChangesHref, sessionRequestsHref } from './trace-links'
import './audit.scss'
import { ErrorReference } from '../error/error-reference'
import { LocalTime } from '../date/local-time-view'
import { useErrorLine } from '../error/use-error-line'
import { copyText } from '../clipboard/clipboard'
import { ManualCopy } from '../clipboard/manual-copy'
import { currentSessionOf, isSameSession, SessionDetail, THIS_SESSION } from './session-label'
import { AppChip, appChipView } from './app-chip'
import { changesBesideHeadline, isView, storyView, viewWords } from './entry-story'
import { FactChips, Headline, PlaceChip, placeView, withinPivotText, withinText } from './place-chip'

// One audit entry (spec §5): who did what to which record, its field
// changes, and Details (the raw changes of the whole request, the network,
// and the pivots). Every display rule -- links, staff, people who left,
// moved and deleted records, version labels -- comes from entry-format.js.
//
// "Sign out this session" (owner ruling 6) is offered by canSignOutSession
// (audit-log readers, a person's own session, never staff or impersonated)
// and revokes by the entry's session UUID (audit-api signOutSession). It
// always asks first, in the sessions pages' own words (signOutSessionBody):
// auth ends the whole session for staff, and an organization admin's
// sign-out every use of it in their organization. A refusal shows the
// server's sentence only when coded.
//
// The entry's session is never shown as its UUID (owner 2026-10-07): the
// reader's own is marked "This session"; Details names it in words
// (session-label.js), and only staff also see the id there.

// A record's name, linked when it can be opened (recordView's href).
const Named = ({ view }) => (view.href ? <Link to={view.href}>{view.text}</Link> : <span className="au-record-name">{view.text}</span>)

// sentence: the entry's sentence it follows, so it never repeats what the
// sentence already says (recordAfterSentence). A join row reads as the
// records its key names, each linked on its own ("owner · Jane Doe").
const RecordRef = ({ refValue, sentence, sys = false }) => {
  const v = sentence === undefined ? recordView(refValue, { sys }) : recordAfterSentence(sentence, refValue, { sys })
  if (!v.text && !v.note) return null
  return (
    <span className="au-record">
      {v.parts
        ? v.parts.map((p, i) => <React.Fragment key={i}>{i > 0 && ' · '}<Named view={p} /></React.Fragment>)
        : v.text && <Named view={v} />}
      {v.secondary && <>{' '}<span className="sw-hint au-note">{v.secondary}</span></>}
      {v.note && <>{v.text ? ' · ' : ''}<span className="sw-hint au-note">{v.note}</span></>}
    </span>
  )
}

// One side of a field row: a referenced record's name (linked when it can
// be opened, with its note: deleted, moved), a time (local, its exact UTC
// time on hover), or the value's text.
const Side = ({ view }) =>
  <>
    {view.href
      ? <Link to={view.href}>{view.text}</Link>
      : view.when ? <time dateTime={view.when} title={exactUTC(view.when)}>{view.text}</time> : view.text}
    {view.note && <span className="sw-hint au-note">{` · ${view.note}`}</span>}
  </>

// A field row's value (changeView): an added value, what a removed row held
// (plainly: never "→ (empty)"), or from → to.
const DiffValue = ({ change, sys }) => {
  const [open, setOpen] = useState(false)
  const v = changeView(change, { sys })
  if (v.kind === 'masked') return <span className="au-diff-cell"><span className="au-diff-value"><ins>{v.text}</ins></span></span>
  const long = ((v.from && v.from.text) || '').length + ((v.to && v.to.text) || '').length > 90
  return (
    <span className="au-diff-cell">
      <span className={`au-diff-value${long && !open ? ' au-clip' : ''}`}>
        {v.kind === 'removed' && <span className="au-removed"><Side view={v.from} /></span>}
        {v.kind === 'changed' && <><del><Side view={v.from} /></del>{' → '}</>}
        {v.to && <ins><Side view={v.to} /></ins>}
      </span>
      {long && <button type="button" className="btn btn-link btn-sm p-0 au-more" aria-expanded={open} onClick={() => setOpen(o => !o)}>{open ? 'Show less' : 'Show more'}</button>}
    </span>
  )
}

export const DiffRows = ({ changes, withRecord, sys = false }) =>
  <div className="au-diff">
    {changes.map((c, i) =>
      <div className="au-diff-row" key={i}>
        <span className="au-diff-field">{withRecord && c.record ? <><RecordRef refValue={c.record} sys={sys} />{' · '}</> : null}{c.label || c.field}</span>
        <DiffValue change={c} sys={sys} />
      </div>)}
  </div>

const KV = ({ rows }) =>
  <dl className="sw-dl au-dl">
    {rows.filter(([, v]) => v).map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}
  </dl>

const COPY = 'Copy link'

// detail: the full entry when the caller already has it (the drawer), so
// Details doesn't read it again.
// historyOf = { type, id }: the record a History drawer is about. Its own
// entries leave the record out (the drawer is already about it); a child's
// entries (an asset's versions) still name the child.
const isRecord = (ref, of) => Boolean(ref && of && ref.type === of.type && String(ref.id) === String(of.id))

// The within filter's patch for a course or folder chip ("<name> and
// everything in it"), with its name for the filter's chip.
export const withinPatch = place => ({ within: `nodes:${place.id}`, withinName: place.name || '' })

// The places an entry offers to narrow to: its course and its folder.
export const withinPlaces = entry => {
  const c = entry && entry.context
  return [c && c.course, c && c.folder].filter(p => p && placeView(p) && placeView(p).within)
}

export const AuditEntry = ({ entry, detail: knownDetail = null, sys = false, caps = {}, onPivot, open: openInitially = false, context, historyOf }) => {
  const auth = useContext(AuthContext)
  const authState = (auth && auth.state) || {}
  const thisSession = isSameSession(entry.sessionID, currentSessionOf(authState))
  const [open, setOpen] = useState(openInitially)
  // Details' full entry, kept while Details is closed: read once.
  const [detail, setDetail] = useState(knownDetail)

  const who = actorView(entry.actor, { sys })
  // The app it came from (app-chip.js): on every entry, every width.
  const app = appChipView(entry, { automatic: who.automatic || who.unattributed })
  const via = viaText(entry, { sys })
  const place = locationText(entry.location)
  const pivot = onPivot ? patch => onPivot(patch) : null
  const onWithin = pivot ? p => pivot(withinPatch(p)) : null
  // A view (a course launched, a page opened: one person, one record, one
  // day) reads by its kind, its count in words after its record ("viewed
  // the assessment Test 3 times today"), never as a change or a "×3" chip
  // (entry-story.js viewWords).
  const view = isView(entry) ? viewWords(entry) : null
  const sentence = view ? `${view.verb} the ${view.noun}` : daySentence(entry.sentence, entry.occurredAt)
  // An entry about content reads in plain words, with its course and folder
  // (entry-story.js); anything else keeps the server's sentence and record.
  const story = entry.context && entry.context.course ? storyView([entry]) : null
  const headline = story && story.headline
  // The record follows the sentence unless the drawer is already about it,
  // or it is the actor's own login or membership (a sign-in).
  const showRecord = (context !== 'history' || (historyOf && !isRecord(entry.record, historyOf))) && !isSelfRecord(entry)
  // A course launch or view of a course is its chip; another record placed
  // in a course says which.
  const course = !headline && entry.context && entry.context.course
  const recordIsCourse = course && entry.record && entry.record.type === 'nodes' && String(entry.record.id) === String(course.id)
  // A creation's or deletion's other fields are under Details only; under a
  // headline, only what it does not already tell.
  const inline = headline ? changesBesideHeadline(entry, inlineChanges(entry)) : inlineChanges(entry)

  return (
    <li className="au-ev">
      <div className={`au-av${who.automatic || who.unattributed || who.support ? ' au-av-sys' : ''}`} aria-hidden="true">{who.initials}</div>
      <div className="au-body">
        <div className="au-line">
          {who.href ? <Link className="au-who" to={who.href}>{who.text}</Link> : <b className="au-who">{who.text}</b>}
          {via && <>{' '}<span className="sw-hint au-note">{via}</span></>}
          {who.note && <>{' · '}<span className="sw-hint au-note">{who.note}</span></>}
          {' '}
          {headline
            ? <Headline segments={headline} sys={sys} onWithin={onWithin} />
            : <>
                <span className="au-sentence">{sentence}</span>{' '}
                {showRecord && (recordIsCourse
                  ? <PlaceChip place={course} sys={sys} onWithin={onWithin} />
                  : <RecordRef refValue={entry.record} sentence={sentence} sys={sys} />)}
                {course && !recordIsCourse && <>{' in '}<PlaceChip place={course} sys={sys} onWithin={onWithin} /></>}
                {view && view.times && <>{showRecord || course ? ' ' : ''}<span className="au-sentence">{view.times}</span></>}
              </>}
          {!view && entry.count > 1 && <>{' '}<Pill tone={entry.unusualLocation ? 'bad' : ''}>{`×${formatCount(entry.count)}`}</Pill></>}
        </div>
        {story && <FactChips chips={story.chips} />}
        {inline.length > 0 && <DiffRows changes={inline} sys={sys} />}
        {entry.moreChanges > 0 && <div className="sw-hint small">{`and ${formatCount(entry.moreChanges)} more ${entry.moreChanges === 1 ? 'change' : 'changes'} (see Details)`}</div>}
        <div className="au-meta">
          <time dateTime={entry.occurredAt} title={exactUTC(entry.occurredAt)}>{formatAgo(entry.occurredAt)}</time>
          {entry.category && <Pill tone={categoryTone(entry.category)}>{categoryLabel(entry.category)}</Pill>}
          {who.automatic && <Pill title="Made by an automatic job or integration">automatic</Pill>}
          {app && <span className="au-meta-break" aria-hidden="true" />}
          {app && <AppChip view={app} />}
          {thisSession && <Pill tone="info" title="Done in the session you're using now">{THIS_SESSION}</Pill>}
          {entry.unusualLocation && place && <span className="au-geo-risk" title="Unusual location for this person">{`⚠ ${place}`}</span>}
          <button type="button" className="btn btn-link btn-sm p-0 au-linkbtn" aria-expanded={open} onClick={() => setOpen(o => !o)}>{open ? 'Hide details' : 'Details'}</button>
        </div>
        {open && <EntryDetails entry={entry} detail={detail} onLoaded={setDetail} sys={sys} caps={caps} onPivot={onPivot} />}
      </div>
    </li>
  )
}

// An entry's Details (spec §5): who, when, where from, the request's raw
// changes (read once, from the permalink), and the pivots. Shared by a
// single entry and each change of a story (audit-story.js).
// detail: the full entry when the caller already has it (the drawer, or
// an earlier opening: onLoaded(d) hands it back), so it isn't read again.
export const EntryDetails = ({ entry, detail: knownDetail = null, onLoaded, sys = false, caps = {}, onPivot }) => {
  const location = useLocation()
  const auth = useContext(AuthContext)
  const authState = (auth && auth.state) || {}
  const thisSession = isSameSession(entry.sessionID, currentSessionOf(authState))
  // staff: the Internal tools view, or a reader holding the platform's audit log
  const staff = sys || Boolean(caps.sysAuditLog)
  // auth ends every use of a session for staff; an organization admin's
  // sign-out reaches their organization's (signOutSessionBody)
  const signsOutEverywhere = Boolean(authState.isSysAdmin || authState.isSysRoot)
  const [detail, setDetail] = useState(knownDetail)
  const [detailError, detailFailure, setDetailError] = useErrorLine()
  const [copyLabel, setCopyLabel] = useState(COPY)
  // The permalink, shown selected, when the browser wouldn't copy it.
  const [manualLink, setManualLink] = useState(null)
  const [confirm, setConfirm] = useState(false)
  const [signout, setSignout] = useState({ busy: false, error: null, done: false })
  const loading = useRef(false)
  const live = useRef(true)
  const timer = useRef(null)

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const loadDetail = async () => {
    if (detail || loading.current) return
    loading.current = true
    setDetailError(null)
    try {
      // a super admin's entry may be in any organization (scope=all)
      const d = await (sys ? getEntry(entry.id, { scope: 'all' }) : getEntry(entry.id))
      if (live.current) setDetail(d || entry)
      if (onLoaded) onLoaded(d || entry)
    } catch (err) {
      if (live.current) setDetailError(auditErrorText(err, "This entry's details couldn't be loaded."), err)
    } finally {
      loading.current = false
    }
  }

  useEffect(() => {
    loadDetail()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Copied inside the click (shared/clipboard; Safari refuses a later
  // write). "Link copied" only after a real copy; otherwise the link is
  // shown, selected, with the shortcut.
  const copyLink = () => {
    const url = `${window.location.origin}${entryPath(entry.id, sys)}`
    copyText(url).then(copied => {
      if (!live.current) return
      setCopyLabel(copied ? 'Link copied' : COPY)
      setManualLink(copied ? null : url)
      if (timer.current) clearTimeout(timer.current)
      if (copied) timer.current = setTimeout(() => live.current && setCopyLabel(COPY), 1500)
    })
  }

  const doSignOut = async () => {
    setSignout({ busy: true, error: null, done: false })
    try {
      await signOutSession(entry.sessionID)
      if (!live.current) return
      setSignout({ busy: false, error: null, done: true })
      setConfirm(false)
    } catch (err) {
      if (live.current) setSignout({ busy: false, error: auditErrorText(err, "That session couldn't be signed out."), failure: err, done: false })
    }
  }

  const who = actorView(entry.actor, { sys })
  const app = appChipView(entry, { automatic: who.automatic || who.unattributed })
  const place = locationText(entry.location)
  const actorKey = actorFilterKey(entry.actor, { sys })
  // the pills naming a person or a place stay one line, in full in their
  // title (a person with no name reads as their email)
  const openText = `Open ${firstName(who.text)}'s profile`
  const onlyText = `Only ${who.automatic ? who.text : firstName(who.text)}`
  const pivot = onPivot ? patch => onPivot(patch) : null
  const openRequest = sys && caps.requestLog && entry.requestID
  // the same session's requests, on the super admin's Request log
  const sessionRequests = sys && caps.requestLog && entry.sessionID ? sessionRequestsHref(entry.sessionID, entry.occurredAt) : null
  // without pivots (an entry's own drawer), "Everything in this request /
  // session" are links to the audit log, for a reader who can open it
  const auditLinks = !pivot && (sys ? caps.sysAuditLog : caps.canAudit)
  const offerSignOut = !signout.done && canSignOutSession(entry, caps)
  const ctx = entry.context || {}
  const raw = ctx.dataType ? `${ctx.dataType}${ctx.versionID ? ` · asset version id ${ctx.versionID}` : ''}` : null

  return (
    <div className="au-detail">
      {detailError && <p className="text-danger small m-0" role="alert">{detailError}</p>}
      {detailError && <ErrorReference error={detailFailure} className="mt-1" />}
      <KV rows={[
        ['Action', <code>{entry.action}</code>],
        ['When', <LocalTime value={entry.occurredAt} />],
        ['Who', [who.text, who.email].filter(Boolean).join(' · ')],
        ['Via', entry.impersonator ? actorView(entry.impersonator, { sys }).text : null],
        ['Type', raw ? <code>{raw}</code> : null],
        ['App', app ? app.full : entry.app],
        ['Network', [entry.ip, place].filter(Boolean).join(' · ')],
        ['Session', entry.sessionID ? <SessionDetail sessionID={entry.sessionID} params={sys ? { scope: 'all' } : {}} staff={staff} /> : null],
        ['Request', entry.requestID ? <code>{entry.requestID}</code> : null]
      ]} />
      {detail && detail.changes && detail.changes.length > 0 &&
        <div>
          <div className="sw-hint small">Database changes in this request</div>
          <DiffRows changes={detail.changes} withRecord sys={sys} />
        </div>}
      <div className="au-actions">
        {who.href && <Link className="au-act au-act-fit" to={who.href} title={openText}><span className="au-act-text">{openText}</span></Link>}
        {pivot && actorKey && <button type="button" className="au-act au-act-fit" title={onlyText} onClick={() => pivot({ actors: [actorKey] })}><span className="au-act-text">{onlyText}</span></button>}
        {pivot && withinPlaces(entry).map(p =>
          <button key={`within-${p.id}`} type="button" className="au-act au-act-fit" title={withinText(placeView(p).name)} onClick={() => pivot(withinPatch(p))}>
            <span className="au-act-text">{withinPivotText(p, { own: p === ctx.course })}</span>
          </button>)}
        {pivot && entry.ip && <button type="button" className="au-act" onClick={() => pivot({ ip: entry.ip })}>Only this IP</button>}
        {pivot && entry.app && <button type="button" className="au-act" onClick={() => pivot({ apps: [entry.app] })}>{`Only ${entry.app}`}</button>}
        {pivot && entry.requestID && <button type="button" className="au-act" onClick={() => pivot({ request: entry.requestID })}>Everything in this request</button>}
        {pivot && entry.sessionID && <button type="button" className="au-act" onClick={() => pivot({ session: entry.sessionID })}>Everything in this session</button>}
        {auditLinks && entry.requestID && <Link className="au-act" to={requestChangesHref(entry.requestID, entry.occurredAt, { sys })}>Everything in this request</Link>}
        {auditLinks && entry.sessionID && <Link className="au-act" to={sessionChangesHref(entry.sessionID, { sys })}>Everything in this session</Link>}
        {offerSignOut && <button type="button" className="au-act au-danger" onClick={() => setConfirm(true)}>Sign out this session…</button>}
        <button type="button" className="au-act" onClick={copyLink}>{copyLabel}</button>
        {openRequest &&
          <Link className="au-act" to={{ pathname: `/admin/request-log/${encodeURIComponent(entry.requestID)}`, search: entry.occurredAt ? `?at=${encodeURIComponent(entry.occurredAt)}` : '', state: { from: { label: 'the audit trail', to: `${location.pathname}${location.search}` } } }}>Open request</Link>}
        {sessionRequests && <Link className="au-act" to={sessionRequests}>Requests in this session</Link>}
      </div>
      {manualLink && <ManualCopy text={manualLink} />}
      {signout.done && <p className="small text-success m-0" role="status">Signed out.</p>}
      <ConfirmDialog
        open={confirm}
        title="Sign out this session?"
        body={thisSession
          ? `${signOutSessionBody('you', { inOrganization: !signsOutEverywhere })} ${YOUR_OTHER_SESSIONS_STAY}`
          : `${signOutSessionBody(who.text, { inOrganization: !signsOutEverywhere })} ${OTHER_SESSIONS_STAY}`}
        confirmText="Sign out"
        busy={signout.busy}
        error={signout.error}
        failure={signout.failure}
        onConfirm={doSignOut}
        onCancel={() => { setConfirm(false); setSignout(s => ({ ...s, error: null })) }}
      />
    </div>
  )
}
